import { Immutable } from "immer";
import React from "react";
import { ProductCompareGridSSR } from "tsi-common-react/src/apps/product-compare/ProductCompareGridSSR/index";
import { ProductSelectionGridSSR } from "tsi-common-react/src/apps/product-compare/ProductSelectionGridSSR/index";
import { BannerBlock } from "tsi-common-react/src/components/BannerBlock/index";
import { ComparisonCarouselBlock } from "tsi-common-react/src/components/ComparisonCarouselBlock/index";
import { EmailCaptureContent } from "tsi-common-react/src/components/EmailCaptureContent/index";
import { ProductCalloutBlock } from "tsi-common-react/src/components/ProductCalloutBlock/index";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";
import { PromotionBlock } from "tsi-common-react/src/components/PromotionBlock/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";

import { templates } from "@reactivated";
import type {
    EmailCaptureBlock as EmailCaptureBlockValue,
    ToppersPromoConditionalBlock as PromoConditionalBlockValue,
    PromotionBlock as PromotionBlockValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { FeaturesModule } from "./FeaturesModule";
import { LifestyleModule } from "./LifestyleModule";

/**
 * Shared Toolbox block dispatcher map for every toppers page-body assembly.
 * Keys are the EXACT block-name strings from the Python ``ToolboxContent`` /
 * ``Toolbox`` (``ToolboxContainers``) StreamBlocks; consumers spread this map
 * and layer their own page-specific overrides on top.
 *
 * Built inside ``buildToolboxComponentsMap`` so the recursive
 * ``promo_conditional_block`` entry can reference the full map for its inner
 * ``content`` StreamBlock (which is itself a ``ToolboxContent``).
 *
 * toppers ``ToolboxContent`` is exactly the 10 simple content keys below;
 * ``Toolbox`` adds the recursive ``promo_conditional_block``. toppers has no
 * ``rich_text`` / ``html`` / ``compatibility`` / ``link`` etc. Toolbox keys, so
 * those entries are intentionally absent here.
 */
const buildToolboxComponentsMap = () => {
    const map = {
        // --- Simple {value} dispatch (component accepts `value` directly) ---
        banner: BannerBlock,
        comparison_carousel_block: ComparisonCarouselBlock,
        features: FeaturesModule,
        lifestyle: LifestyleModule,
        product_callout: ProductCalloutBlock,

        // `value_prop_bar_block` -> ValuePropsBarBlock (prop is `valuePropsData`).
        value_prop_bar_block: (block: {
            value: Immutable<ValuePropBarBlockValue>;
        }) => <ValuePropsBarBlock valuePropsData={block.value} />,

        // `promotion_block` -> needs 1-indexed `blockIndex` for the GA4
        // `view_promotion` creative_slot.
        promotion_block: (block: {
            index?: number;
            value: Immutable<PromotionBlockValue>;
        }) => (
            <PromotionBlock
                value={block.value}
                blockIndex={(block.index ?? 0) + 1}
            />
        ),

        // `email_capture` -> reproduces the shared email-capture-block.html: maps
        // the block's `email_snippet` into the EmailCaptureContent shape and
        // wraps it in the themed `<section>` (background_style +
        // responsive_visibility classes the standalone template adds). The
        // responsive_visibility class is the functional half -- it hides the
        // block per breakpoint, so dropping the wrapper could surface a block
        // the CMS configured hidden. Renders nothing when the snippet is absent.
        email_capture: (block: {
            value: Immutable<EmailCaptureBlockValue>;
        }) => {
            const snippet = block.value.email_snippet;
            if (!snippet) {
                return null;
            }
            return (
                <section
                    className={concatClassNames([
                        "email-capture-block",
                        block.value.background_style?.style ?? "",
                        block.value.responsive_visibility?.visibility ?? "",
                    ])}
                >
                    <EmailCaptureContent
                        content={{
                            email_snippet: {
                                title: snippet.title ?? "",
                                content: snippet.content,
                                confirm_content: snippet.confirm_content,
                                image: snippet.image,
                                footnotes: snippet.footnotes,
                            },
                        }}
                    />
                </section>
            );
        },

        // --- SSR-envelope blocks (`block.value` carries the full server-
        // computed SSR props) ---

        // `product_compare_grid` -> ProductCompareGridSSR. Plain spread; the
        // toppers ProductCompareGridSSREnvelope is structurally the SSR
        // component's props (cms_data, base_tiles_data, products_by_id_data,
        // variant, initial_compare_tile_ids, return_url).
        product_compare_grid: (block: {
            value: Immutable<templates.ProductCompareGridBlockTemplate> | null;
        }) => (block.value ? <ProductCompareGridSSR {...block.value} /> : null),

        // `product_selection_grid` -> ProductSelectionGridSSR. Plain spread.
        product_selection_grid: (block: {
            value: Immutable<templates.ProductSelectionGridBlockTemplate> | null;
        }) =>
            block.value ? <ProductSelectionGridSSR {...block.value} /> : null,

        // --- RECURSIVE: `promo_conditional_block` (Toolbox / ToolboxContainers) ---
        // Its `content` StreamBlock IS a ToolboxContent, so it dispatches via
        // this same map. `map` is captured by reference below.
        promo_conditional_block: (block: {
            value: Immutable<PromoConditionalBlockValue>;
        }) => (
            <PromoConditionalBlock value={block.value}>
                <StreamField
                    tagName={React.Fragment}
                    value={block.value.content ?? []}
                    components={map}
                />
            </PromoConditionalBlock>
        ),
    } as const;
    return map;
};

export const toolboxComponentsMap = buildToolboxComponentsMap();
